import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useId, useLayoutEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { setAlpha } from '../../styles';
import type { ButtonProps } from '../Button/styles';
import { getIconSize } from '../Button/styles';
import type { BoxProps } from '../Core';
import { FormControlSizes } from '../Form/types';
import { Icon, IconName } from '../Icons';
import { Popover, usePopoverState } from '../Popover';
import { DropdownList } from './DropdownList';
import { useDropdownListContext } from './DropdownListContext';
import { DropdownItemWrapper } from './styles';

const SUBMENU_CHEVRON_OPACITY = 0.56;

export type DropdownItemProps = PropsWithChildren<
  ButtonProps & {
    /** Declarative submenu content rendered inside a nested DropdownList. Accepts DropdownItem, Divider, DropdownHeader, etc. */
    submenuContent?: ReactNode;
    /** Props forwarded to the submenu DropdownList wrapper (e.g. data-testid, minWidth) */
    submenuListProps?: BoxProps & { 'data-testid'?: string };
    /** Document for submenu portal (e.g. popout window); omit for default document */
    popoverDocument?: Document;
  }
>;

const POPOVER_TARGET_STYLE = { display: 'block' as const };

/**
 * A composable menu item that can be nested within a {@link DropdownList}.
 *
 * Supports keyboard and hover highlighting, optional start/end icons,
 * and declarative nested submenus via {@link DropdownItemProps.submenuContent submenuContent}.
 * When a submenu is provided, the item renders a chevron indicator and manages
 * a {@link Popover}-hosted child {@link DropdownList} that can be entered with
 * ArrowRight and exited with ArrowLeft.
 */
export function DropdownItem({
  children,
  submenuContent,
  submenuListProps,
  popoverDocument,
  ...props
}: DropdownItemProps) {
  // Avoid rendering a submenu if the submenuContent is empty
  const hasSubmenu = !!submenuContent;
  const generateId = useId();
  const id = props.id ?? generateId;
  const submenuListRef = useRef<HTMLDivElement | null>(null);
  const { colorTextImportant } = useTheme();
  const {
    isActive: inList,
    registerItem,
    unregisterItem,
    setHighlightFromHover,
    isHighlighted,
    listRef,
  } = useDropdownListContext();

  const submenuPopover = usePopoverState({
    placement: 'right-start',
    trigger: 'hover',
    usePortal: true,
    popoverDocument,
    noPaddingAndBorder: true,
    delay: 200,
    hideDelay: 150,
  });

  const submenuPopoverRef = useRef(submenuPopover);
  submenuPopoverRef.current = submenuPopover;

  const openSubmenu = useCallback(() => {
    submenuPopoverRef.current.open();
  }, []);

  const closeSubmenu = useCallback(() => {
    submenuPopoverRef.current.close();
  }, []);

  useLayoutEffect(() => {
    if (!inList) {
      return;
    }
    registerItem(id, {
      hasSubmenu,
      openSubmenu: hasSubmenu ? openSubmenu : undefined,
      closeSubmenu: hasSubmenu ? closeSubmenu : undefined,
      focusSubmenuList: hasSubmenu ? () => submenuListRef.current?.focus() : undefined,
    });
    return () => unregisterItem(id);
  }, [registerItem, unregisterItem, id, hasSubmenu, openSubmenu, closeSubmenu, inList]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setHighlightFromHover(id);
      props.onMouseEnter?.(e);
    },
    [setHighlightFromHover, id, props]
  );

  const menuItem = (
    <DropdownItemWrapper
      ghost
      {...props}
      id={inList ? id : undefined}
      role={inList ? 'menuitem' : undefined}
      data-dropdown-item-id={inList ? id : undefined}
      data-highlighted={inList && isHighlighted(id) ? '' : undefined}
      isHighlighted={inList && isHighlighted(id)}
      onMouseEnter={inList ? handleMouseEnter : props.onMouseEnter}
      {...(hasSubmenu && {
        endIcon: undefined,
        isSubmenuOpen: submenuPopover.isOpen,
        suffix: props.suffix ?? (
          <Icon
            icon={IconName.ChevronRight}
            size={getIconSize(props.size ?? FormControlSizes.Default)}
            color={setAlpha(SUBMENU_CHEVRON_OPACITY, colorTextImportant)}
          />
        ),
      })}
    >
      {children}
    </DropdownItemWrapper>
  );

  if (!hasSubmenu) {
    return menuItem;
  }

  return (
    <Popover {...submenuPopover} targetStyle={POPOVER_TARGET_STYLE}>
      {menuItem}
      <DropdownList
        ref={submenuListRef}
        onArrowLeftToParent={() => {
          closeSubmenu();
          listRef.current?.focus();
        }}
        minWidth="180px"
        {...submenuListProps}
      >
        {submenuContent}
      </DropdownList>
    </Popover>
  );
}
